import { Editor, Html } from "@mixitone/components";
import clsx from "clsx";
import { isMemberNoteBlank, normalizeMemberNoteHtml } from "lib/memberNoteHtml";
import { memo, useCallback, useEffect, useRef, useState } from "react";

interface Props {
  value: string;
  placeholder: string;
  readOnly?: boolean;
  saving?: boolean;
  onSave: (value: string) => void | Promise<void>;
}

const NOTE_HTML_CLASS =
  "w-full min-w-0 max-w-full break-words text-sm leading-relaxed [&_a]:text-blue-600 [&_a]:underline [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5";

interface EditorSessionProps {
  initialValue: string;
  disabled?: boolean;
  onCommit: (html: string) => void;
  onCancel: () => void;
}

const MemberNoteEditorSession = memo(function MemberNoteEditorSession({
  initialValue,
  disabled,
  onCommit,
  onCancel,
}: EditorSessionProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const htmlRef = useRef(initialValue);
  const onCommitRef = useRef(onCommit);
  const onCancelRef = useRef(onCancel);
  onCommitRef.current = onCommit;
  onCancelRef.current = onCancel;

  const [html, setHtml] = useState(initialValue);

  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (target && containerRef.current?.contains(target)) return;
      onCommitRef.current(normalizeMemberNoteHtml(htmlRef.current));
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCancelRef.current();
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="[&_.rsw-ce]:min-h-[120px] [&_.rsw-editor]:min-h-[160px] [&_.rsw-editor]:rounded-lg"
    >
      <Editor
        autoFocus
        variant="compact"
        value={html}
        onChange={(event) => {
          htmlRef.current = event.target.value;
          setHtml(event.target.value);
        }}
        disabled={disabled}
      />
    </div>
  );
});

const MemberNoteField: React.FC<Props> = ({ value, placeholder, readOnly, saving, onSave }) => {
  const [editing, setEditing] = useState(false);
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  const handleCommit = useCallback(
    async (html: string) => {
      const normalized = normalizeMemberNoteHtml(html);
      const previous = normalizeMemberNoteHtml(value);
      setEditing(false);
      if (normalized !== previous) {
        await onSaveRef.current(normalized);
      }
    },
    [value],
  );

  const handleCancel = useCallback(() => {
    setEditing(false);
  }, []);

  if (readOnly) {
    return (
      <div
        className={clsx("min-h-[160px] min-w-0 max-w-full", {
          "italic text-gray-500": isMemberNoteBlank(value),
        })}
      >
        {isMemberNoteBlank(value) ? "No note" : <Html html={value} className={NOTE_HTML_CLASS} />}
      </div>
    );
  }

  if (!editing) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => setEditing(true)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setEditing(true);
          }
        }}
        className={clsx(
          "min-h-[160px] w-full min-w-0 max-w-full cursor-text rounded-lg border border-transparent p-3 text-left transition-colors",
          "hover:border-gray-300 hover:bg-gray-50 focus-visible:border-gray-300 focus-visible:bg-gray-50 focus-visible:outline-none",
          saving && "pointer-events-none opacity-60",
        )}
      >
        {isMemberNoteBlank(value) ? (
          <span className="text-sm italic text-gray-500">{placeholder}</span>
        ) : (
          <Html html={value} className={NOTE_HTML_CLASS} />
        )}
      </div>
    );
  }

  return (
    <MemberNoteEditorSession
      initialValue={value}
      disabled={saving}
      onCommit={handleCommit}
      onCancel={handleCancel}
    />
  );
};

export default MemberNoteField;
